<template>
    <div class="row clickable" :class="['no-img', audience.mine ? 'created-by' : '']" @click.stop="openAudience(false)">
        <div class="top">
            <div class="content">
                <div class="name">{{audience.name || '-'}}</div>
                <p class="description" v-if="audience.inserted_time === audience.changed_time" v-html="$t('views.audiences.items.description', {total: formatNumber(audience.total), date: epochToDisplayDate(audience.inserted_time || 0), name: owner.email || owner.name || '..?'})"></p>
                <p class="description" v-else v-html="$t('views.audiences.items.description_modified', {total: formatNumber(audience.total), date: epochToDisplayDate(audience.inserted_time || 0), dateModified: epochToDisplayDate(audience.changed_time || 0), name: owner.email || owner.name || '..?'})"></p>
            </div>

            <div class="actions">
                <button class="btn-2 btn-s-icon" @click.stop="openAudience('companies')" v-tooltip="'tooltips.audience_companies'">
                    <div class="icon-company-neg icon-xxs"></div>
                </button>
                <button class="btn-7 btn-s-icon" :class="{'btn-paywall': paywall}" :disabled="paywall || prospectingCompleted" @click.stop="openAudience('new')" v-tooltip="'tooltips.audience_suspects'">
                    <div v-if="paywall" class="paywall-lock"></div>
                    <div class="icon-new-neg icon-xxs"></div>
                </button>
                <button class="btn-1 btn-s-icon" @click.stop="openAudience('prospects')" v-tooltip="'tooltips.audience_prospects'">
                    <div class="icon-target-neg icon-xxs"></div>
                </button>
                <button class="btn-5 btn-s-icon" @click.stop="cloneAudiencePopup()" v-tooltip="'tooltips.audience_clone'">
                    <div class="icon-copy-neg icon-xxs"></div>
                </button>
                <button class="btn-6 btn-s-icon" v-if="audience.editable" @click.stop="deleteAudiencePopup" v-tooltip="'tooltips.trash'">
                    <div class="icon-trash-neg icon-xxs"></div>
                </button>
            </div>
        </div>

        <div class="details" v-if="audience.stats">
            <ul class="stats">
                <li class="total">
                    <div class="value">{{formatNumber(audience.stats.suspects)}}</div>
                    <div class="p title">{{$t('views.audiences.items.total')}}</div>
                </li>
                <li class="total">
                    <div class="value">{{formatNumber(audience.stats.prospects)}}</div>
                    <div class="p title">{{$t('views.audiences.items.prospects')}}</div>
                </li>
                <li>
                    <div class="value">{{perc(audience.stats.suspects, audience.stats.prospects || 0)}}<span>%</span></div>
                    <div class="p title">{{$t('views.audiences.items.seen')}}</div>
                </li>
                <li v-if="audience.stats.label && Object.keys(audience.stats.label).length > 0">
                    <div class="value">{{perc(audience.stats.suspects, audience.stats.label['1'] + audience.stats.label['0'] || 0)}}<span>%</span></div>
                    <div class="p title">{{$t('views.audiences.items.qualified')}}</div>
                </li>

                <li v-if="audience.stats.with_visits || audience.stats.with_visits === 0">
                    <div class="value">{{perc(audience.stats.suspects,audience.stats.with_visits || 0)}}<span>%</span></div>
                    <div class="p title">{{$t('views.audiences.items.with_visits')}}</div>
                </li>

                <li v-if="audience.stats.with_contacts || audience.stats.with_contacts === 0">
                    <div class="value">{{perc(audience.stats.suspects,audience.stats.with_contacts || 0)}}<span>%</span></div>
                    <div class="p title">{{$t('views.audiences.items.with_contact')}}</div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import i18n from "@/plugins/i18n";
    import { markRaw } from 'vue';
    import { mapActions, mapState } from 'pinia';
    import { useAudiencesStore } from '@/stores/audiences';
    import { useUsersStore } from '@/stores/users';
    import { usePopupStore } from '@/stores/utils/popup';
    import { useAccountStore } from '@/stores/account';
    import { useToastStore } from '@/stores/utils/toast';
    import { epochToDisplayDate } from '@/utils/dates';
    import { formatNumber } from '@/utils/formatting';
    import AudienceCloneForm from '@/components/forms/AudienceCloneForm';

    export default {
        name: 'AudiencesListItem',
        props: {
            audience: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                loaded: false
            }
        },
        computed: {
            ...mapState(useUsersStore, ['users']),
            ...mapState(useAccountStore, ['paywall']),
            audiencesStore() {
                return useAudiencesStore();
            },
            toastStore() {
                return useToastStore();
            },
            popupStore() {
                return usePopupStore();
            },
            accountStore() {
                return useAccountStore();
            },
            owner() {
                return this.users.filter(user => this.audience.created_by || this.audience.changed_by === user.uid)[0] || false;
            },
            prospectingCompleted() {
                return this.audience?.stats?.prospects && this.audience?.stats?.suspects && this.audience.stats.prospects === this.audience.stats.suspects;
            }
        },
        methods: {
            epochToDisplayDate,
            formatNumber,
            ...mapActions(useUsersStore, ['getUsers']),
            ...mapActions(useAudiencesStore, ['fetchAudiences', 'resetAudiences']),
            perc(total, value) {
                return Math.round((value / total) * 100).toFixed(0);
            },
            openAudience(mode = false) {
                this.audiencesStore.openAudience(this.audience, mode);
            },
            cloneAudiencePopup() {
                usePopupStore().showPopup('form', {
                    title: 'popups.clone_audience.title',
                    description: 'popups.clone_audience.description',
                    formComponent: markRaw(AudienceCloneForm),
                    formProps: {
                        audience: this.audience
                    },
                    translationPlaceholders: {
                        description: {
                            name: this.audience.name
                        }
                    },
                    onFormSuccess: () => {
                        this.toastStore.addToast(i18n.global.t('toasts.success.audience_cloned'), 'success');
                        this.resetAudiences();
                        this.fetchAudiences();
                    }
                });
            },
            deleteAudiencePopup() {
                this.popupStore.showPopup('confirm', {
                    title: 'popups.confirm_audience_delete.title',
                    description: 'popups.confirm_audience_delete.description',
                    btnConfirm: "popups.confirm_audience_delete.button_confirm",
                    danger: true,
                    confirmFunction: this.deleteAudience,
                    translationPlaceholders: {
                        description: {
                            audienceName: this.audience.name || '-',
                        }
                    }
                });
            },
            async deleteAudience() {                
                try {
                    const response = await this.audiencesStore.deleteAudience(this.audience);
                    if(response) {
                        this.toastStore.addToast(i18n.global.t('toasts.success.audience_deleted'), 'success');
                    } else {
                        this.toastStore.addToast(i18n.global.t('toasts.error.audience_deletion_failed'), 'error');
                    } 
                } catch {
                    this.toastStore.addToast(i18n.global.t('toasts.error.audience_deletion_failed'), 'error');   
                }
            }
        },
        async mounted() {
            await this.getUsers();
            this.loaded = true;
        },
    }
</script>